// src/components/Admin/Email/NewMessageModal.tsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
  Chip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextFieldVariants,
} from "@mui/material";
import { DateTimePicker } from "@mui/lab";
import Autocomplete from "@mui/material/Autocomplete";
import { useEmailService, SendEmailPayload } from "../../../services/api/emailService";
import { useEmailContext } from "../../../contexts/EmailContext";
import { ImapEmail } from "../../../types/imapEmail";
import { PRE_MADE_TEMPLATES } from "../../../constants/emailTemplates";
import { JSX } from "react/jsx-runtime";

/** You might store possible fromEmails somewhere, e.g. in an array or from user settings. */
const FROM_EMAIL_OPTIONS = [
  "admin@hbinternational.org",
  "support@hbinternational.org",
  "info@hbinternational.org"
];

interface NewMessageModalProps {
  open: boolean;
  onClose: () => void;
  onMessageSent: (email: ImapEmail) => void;
  initialTo?: string;
}

const NewMessageModal: React.FC<NewMessageModalProps> = ({
  open,
  onClose,
  onMessageSent,
  initialTo = "",
}) => {
  const { emails } = useEmailContext();
  const { sendEmail } = useEmailService();

  // Fields
  const [fromEmail, setFromEmail] = useState<string>(FROM_EMAIL_OPTIONS[0]); // default
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Optional robust template fields
  const [recipientName, setRecipientName] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [signature, setSignature] = useState("");

  // Pre-made template selection
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");

  useEffect(() => {
    setTo(initialTo);
  }, [initialTo]);

  // Extract unique email addresses for autocomplete suggestions
  const emailSuggestions = useMemo(() => {
    const suggestionsSet = new Set<string>();
    emails.forEach((email) => {
      if (typeof email.from === "object" && email.from.email) {
        suggestionsSet.add(email.from.email);
      } else if (typeof email.from === "string") {
        suggestionsSet.add(email.from);
      }
      if (Array.isArray(email.to)) {
        email.to.forEach((r) => suggestionsSet.add(r));
      }
    });
    return Array.from(suggestionsSet);
  }, [emails]);

  /**
   * Convert each attached File into the base64 object expected by the server.
   */
  const convertFilesToBase64 = async (files: File[]): Promise<SendEmailPayload["attachments"]> => {
    const readFileAsBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result === "string") resolve(result.split(",")[1]); // remove "data:xxx;base64,"
          else reject(new Error("File reading failed"));
        };
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });
    };

    const base64Attachments = [];
    for (const file of files) {
      const base64Data = await readFileAsBase64(file);
      base64Attachments.push({
        filename: file.name,
        base64Data,
        contentType: file.type || "application/octet-stream",
      });
    }
    return base64Attachments;
  };

  /**
   * Handle pre-made template selection
   */
  const handleTemplateChange = (templateLabel: string) => {
    setSelectedTemplate(templateLabel);
    if (templateLabel) {
      const template = PRE_MADE_TEMPLATES.find((t) => t.label === templateLabel);
      if (template) {
        setSubject(template.subject || "");
        setMessage(template.message || "");
        setCtaUrl(template.ctaUrl || "");
        setCtaLabel(template.ctaLabel || "");
        setSignature(template.signature || "");
      }
    } else {
      setSubject("");
      setMessage("");
      setCtaUrl("");
      setCtaLabel("");
      setSignature("");
    }
  };

  /**
   * Add new files to attachments list
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments((prev) => [...prev, ...Array.from(e.target.files || [])]);
      e.target.value = "";
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Send the email
   */
  const handleSend = async () => {
    setLoading(true);
    setError("");

    try {
      // Convert attachments to base64
      const base64Attachments = await convertFilesToBase64(attachments);

      // Build the payload for the new /emails/send endpoint
      const payload: SendEmailPayload = {
        fromEmail,
        recipient: to,
        subject,
        message,
        attachments: base64Attachments?.length ? base64Attachments : undefined,
        scheduledTime: scheduledTime ? scheduledTime.toISOString() : undefined,
        recipientName: recipientName || undefined,
        ctaUrl: ctaUrl || undefined,
        ctaLabel: ctaLabel || undefined,
        signature: signature || undefined,
      };

     ////console.log("Final payload to send:", payload);

      // Make the API call
      const response = await sendEmail(payload);
      /** 
       * The new /emails/send likely returns something like:
       * {
       *   success: true,
       *   message: "Email sent successfully",
       *   emailId: "...",
       *   attachments: [...],
       *   timestamp: ...
       * }
       * 
       * If you want to update the UI or do something with the "storedEmail",
       * you'll adapt accordingly.
       */
      if (response.success) {
        // If your onMessageSent expects an ImapEmail, you might do partial. 
        // Or you can remove onMessageSent if you don't need it anymore.
        onMessageSent({
          id: response.emailId || "temp",
          folder: "sent",
          subject,
          from: { email: fromEmail, name: "Hope Beyond Intl." },
          to: [to],
          date: new Date().toISOString(),
          body: message,
          attachments: response.attachments || [],
        } as ImapEmail);

        // Reset fields
        setFromEmail(FROM_EMAIL_OPTIONS[0]);
        setTo("");
        setSubject("");
        setMessage("");
        setAttachments([]);
        setScheduledTime(null);
        setRecipientName("");
        setCtaUrl("");
        setCtaLabel("");
        setSignature("");
        setSelectedTemplate("");

        onClose();
      } else {
        setError("Failed to send email.");
      }
    } catch (err: any) {
      console.error("Error sending email:", err);
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New Message</DialogTitle>
      <DialogContent dividers>
        {/* From email field */}
        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
          <InputLabel>From Email</InputLabel>
          <Select
            label="From Email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value as string)}
          >
            {FROM_EMAIL_OPTIONS.map((emailOption) => (
              <MenuItem key={emailOption} value={emailOption}>
                {emailOption}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Template Selector */}
        <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
          <InputLabel>Choose a Template</InputLabel>
          <Select
            label="Choose a Template"
            value={selectedTemplate}
            onChange={(e) => handleTemplateChange(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {PRE_MADE_TEMPLATES.map((template) => (
              <MenuItem key={template.label} value={template.label}>
                {template.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* "To" field with autocomplete */}
        <Autocomplete
          freeSolo
          options={emailSuggestions}
          value={to}
          onChange={(_, newValue) => {
            if (typeof newValue === "string") {
              setTo(newValue);
            } else if (newValue) {
              setTo(newValue);
            }
          }}
          onInputChange={(_, newInputValue) => {
            setTo(newInputValue);
          }}
          renderInput={(params) => (
            <TextField {...params} label="To" margin="normal" fullWidth />
          )}
        />

        {/* Additional fields for personalization */}
        <TextField
          label="Recipient Name (optional)"
          fullWidth
          margin="normal"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
        />

        <TextField
          label="Subject"
          fullWidth
          margin="normal"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        <TextField
          label="Message"
          multiline
          rows={8}
          fullWidth
          margin="normal"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        {/* Optional CTA fields */}
        <TextField
          label="CTA URL (optional)"
          fullWidth
          margin="normal"
          value={ctaUrl}
          onChange={(e) => setCtaUrl(e.target.value)}
        />
        <TextField
          label="CTA Label (optional)"
          fullWidth
          margin="normal"
          value={ctaLabel}
          onChange={(e) => setCtaLabel(e.target.value)}
        />

        {/* Signature */}
        <TextField
          label="Custom Signature (optional)"
          multiline
          rows={2}
          fullWidth
          margin="normal"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
        />

        {/* Attachments */}
        <Box sx={{ my: 2 }}>
          <Button variant="outlined" component="label">
            Add Attachment
            <input type="file" hidden multiple onChange={handleFileChange} />
          </Button>
        </Box>
        {attachments.length > 0 && (
          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {attachments.map((file, index) => (
              <Chip
                key={index}
                label={file.name}
                onDelete={() => handleRemoveAttachment(index)}
              />
            ))}
          </Box>
        )}

        {/* Scheduling (optional) */}
        <DateTimePicker
          label="Schedule Email"
          value={scheduledTime}
          onChange={(newValue: Date | null) => setScheduledTime(newValue)}
          renderInput={(params: JSX.IntrinsicAttributes & { variant?: TextFieldVariants | undefined; } & Omit<OutlinedTextFieldProps | StandardTextFieldProps | FilledTextFieldProps, "variant">) => (
            <TextField {...params} fullWidth sx={{ mb: 2 }} />
          )}
        />

        {/* Error display */}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSend} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewMessageModal;
